import Factory from '@leverege/factory'

import Isa from './Isa.js'
import { PLUGIN_ADDED, PLUGIN_REMOVED, CREATOR_ADDED, CREATOR_REMOVED, CREATOR_CHANGED } from './Constants.js'
 
/**
 * This presents an optionally filtered factory for a type of plugin.
 * It will listen to the Plugins and automatically update as needed.
 * The values in a plugin that specify the type and object given to
 * factory.add( type, object ) can be specified by pluginTypeKey (default 'type')
 * and pluginKey ( default 'plugin' )
 */
export default class ActiveFactory extends Factory {

  constructor( plugins, pluginType, options ) {
    super( options )
    const { filter = null, pluginTypeKey = 'type', pluginKey } = options || { }
    
    this.plugins = plugins
    this.pluginKey = pluginKey
    this.pluginType = pluginType
    this.pluginTypeKey = pluginTypeKey
    this.useIsa = options?.useIsa === true
    this.dirty = false
    this.filter = filter
    this.listener = ( event ) => { this.onEvent( event ) }
    this.start()
  }

  start( ) {
    this.rebuild()
    this.plugins.on( this.pluginType, this.listener )
  }

  stop( ) {
    this.plugins.off( this.pluginType, this.listener )
  }

  getAll( ...args ) {
    if ( this.dirty ) {
      this.rebuild()
    }
    return super.getAll( ...args )
  }

  get( typeOrObj, returnDefault = true ) {
    if ( this.dirty ) {
      this.rebuild()
    }
    let type = this.getType( typeOrObj )
    if ( !this.useIsa || this.registry[type] ) {
      return this.registry[type] || ( returnDefault ? this.defaultObject : null )
    }
    const singleton = Isa.getSingleton()
    if ( singleton ) {
      const isa = singleton.get( type, false )
      for ( let n = 0; n < isa.length; n++ ) {
        type = this.getType( isa[n] )
        if ( this.registry[type] ) {
          return this.registry[type]
        }
      }
    }
    return returnDefault ? this.defaultObject : null
  }

  create( ...args ) {
    if ( this.dirty ) {
      this.rebuild()
    }
    return super.create( ...args )
  }

  getPlgObj( plg ) {
    if ( !plg ) {
      return null
    }
    if ( this.pluginKey ) {
      return plg[this.pluginKey]
    }
    return plg
  }

  onEvent( evt ) {
    if ( evt.type === CREATOR_ADDED ||
         evt.type === CREATOR_REMOVED ||
         evt.type === CREATOR_CHANGED ) {
      this.dirty = true
      return
    }
    const plg = evt.plugin
    const type = plg && plg[this.pluginTypeKey] 
    const obj = this.getPlgObj( plg )
    if ( type == null || obj == null ) {
      return // no action required
    }
    switch ( evt.type ) {
      case PLUGIN_ADDED: 
        if ( this.filter && this.filter( plg ) === false ) {
          return // filtered out
        }
        if ( type == null ) {
          // eslint-disable-next-line no-console
          console.log( 'Warning: Plugin type has null/undefined type', plg )
          break
        }
        this.add( type, obj, true )
        break
      case PLUGIN_REMOVED: 
        if ( this.get( type ) === obj ) {
          this.remove( type )
          // TODO: so if we had multiple at this type, and we remove the one install,
          // we should try to install the overridden one.
        }
        break

      default: 
    }
  }

  rebuild( ) {
    this.dirty = false
    const opts = this.options
    this.registry = opts.registry ? { ...opts.registry } : { }
    let plgs = this.plugins.get( this.pluginType )
    if ( this.filter ) {
      plgs = plgs.filter( this.filter )
    }
    plgs.forEach( ( plg ) => {
      const type = plg && plg[this.pluginTypeKey] 
      const obj = this.getPlgObj( plg )
      if ( type != null && obj != null ) {
        this.add( type, obj, true )
      }
    } )
  }

}
